<template>
  <base-carousel
    ref="carousel"
    v-bind="$props"
    data-test-id="vf-carousel"
    @init="$emit('init', $event)"
    @paginate="$emit('paginate', $event)"
    @scroll-end="$emit('scroll-end', $event)"
    @scroll-start="$emit('scroll-start', $event)"
    @to-next="$emit('to-next', $event)"
    @to-prev="$emit('to-prev', $event)"
  >
    <template #prev="{ active, action }">
      <!-- @slot Slot for handler that moves content to the previous portion -->
      <slot name="prev" v-bind="{ active, action }">
        <base-button
          :aria-label="$t.previous"
          :class="[
            (!loop || !scrollable) && !active && ['pointer-events-none', brandClasses.controlsInactive],
            dir ? brandClasses.previousControlVertical : brandClasses.previousControlHorizontal,
            brandClasses.controls,
            classControls,
          ]"
          data-test-id="vf-carousel-arrow-left"
          :disabled="(!loop || !scrollable) && !active"
          :style="styleControls"
          @click.stop="action"
        >
          <vf-icon :dir="dir ? 'up' : 'left'" name="chevron" :size="sizeControls || controlsSize || 'lg'" />
        </base-button>
      </slot>
    </template>

    <template #default="data">
      <!-- @slot Slot for list of items -->
      <slot v-bind="data" />
    </template>

    <template #next="{ active, action }">
      <!-- @slot Slot for handler that moves content to the next portion -->
      <slot name="next" v-bind="{ active, action }">
        <base-button
          :aria-label="$t.next"
          :class="[
            (!loop || !scrollable) && !active && ['pointer-events-none', brandClasses.controlsInactive],
            dir ? brandClasses.nextControlVertical : brandClasses.nextControlHorizontal,
            brandClasses.controls,
            classControls,
          ]"
          data-test-id="vf-carousel-arrow-right"
          :disabled="(!loop || !scrollable) && !active"
          :style="styleControls"
          @click.stop="action"
        >
          <vf-icon :dir="dir ? 'down' : 'right'" name="chevron" :size="sizeControls || controlsSize || 'lg'" />
        </base-button>
      </slot>
    </template>

    <template #pagination="data">
      <!-- @slot Slot for pagination handlers -->
      <slot name="pagination" v-bind="data" />
    </template>

    <template #bottom>
      <slot name="bottom" />
    </template>
  </base-carousel>
</template>

<script lang="ts" setup>
import type { BaseCarousel as BaseCarouselType } from '#components'
import type { CSSClass } from '#types/common'
import type { ScrollState } from '#types/components/base/scroll'
import type { IconSizes } from '#types/sizes'
import type { StyleValue } from 'vue'

export type Props = {
  /**
   * Defines autoplay mode.
   * If true slides change automatically
   */
  autoplay?: boolean
  /**
   * Defines interval for slides changing
   * Relevant if `autoplay` prop provided only
   */
  interval?: number
  /**
   * Defines arrows visibility
   * If true arrows become always visible and that makes possible to scroll infinitely
   */
  loop?: boolean
  /**
   * Defines carousel direction
   */
  dir?: 'vertical'
  /**
   * Defines HTML tag of items container
   */
  tag?: string
  /**
   * Defines classList of items container
   */
  classContainer?: CSSClass
  /**
   * Defines styles of items container
   */
  styleContainer?: StyleValue
  /**
   * Defines classList of all content's wrapper excluding pagination section
   */
  classWrapper?: CSSClass
  /**
   * Defines styles of all content's wrapper excluding pagination section
   */
  styleWrapper?: StyleValue
  /**
   * Defines classList of controls
   */
  classControls?: CSSClass
  /**
   * Defines styles of controls container
   */
  styleControls?: StyleValue
  /**
   * Defines size of controls icon
   */
  sizeControls?: IconSizes
  /**
   * Defines enablement of using resize observer
   */
  observeResize?: boolean
}
defineProps<Props>()

defineEmits<{
  /**
   * Emits when BaseScroll component inits
   */
  'init': [payload: ScrollState]
  /**
   * Emits when content moves to prev portion
   */
  'to-prev': [payload: ScrollState]
  /**
   * Emits when content moves to next portion
   */
  'to-next': [payload: ScrollState]
  /**
   * Emits when content moves by clicking on pagination
   */
  'paginate': [payload: ScrollState]
  /**
   * Emits when scrolling starts
   */
  'scroll-start': [payload: ScrollState]
  /**
   * Emits when scrolling ends
   */
  'scroll-end': [payload: ScrollState]
}>()

defineSlots<{
  prev: (props: { active: boolean, action: () => void }) => void
  default: (props: { active: number, activeItem: number, action: (i: number) => void }) => void
  next: (props: { active: boolean, action: () => void }) => void
  pagination: (props: { active: number, activeItem: number, action: (i: number) => void, pages: number }) => void
  bottom: () => void
}>()

const { brandClasses, controlsSize } = useAppConfig().components.vf.carousel
const carousel = ref<InstanceType<typeof BaseCarouselType>>()
const scrollable = computed(() => carousel.value?.scroll?.hasNext || carousel.value?.scroll?.hasPrev)

defineExpose({
  el: computed(() => carousel.value?.el),
  scroll: computed(() => carousel.value?.scroll),
  scrollable
})
</script>
